import { LancerActionTracker } from "@thilke/ui/components/lancer-action-tracker";
import { LancerCounter } from "@thilke/ui/components/lancer-counter";
import { LancerResourceBar } from "@thilke/ui/components/lancer-resource-bar";
import { LancerStatCard } from "@thilke/ui/components/lancer-stat-card";
import { LancerStructureStress } from "@thilke/ui/components/lancer-structure-stress";
import { cn } from "@thilke/ui/lib/utils";
import type * as React from "react";

export interface LancerMechResource {
  id: string;
  label: string;
  value: number;
  max: number;
  tone?: "default" | "success" | "warning" | "destructive" | "info" | "accent";
}

export interface LancerMechCounter {
  value: number;
  max: number;
}

export interface LancerMechStat {
  id: string;
  label: string;
  value: React.ReactNode;
  tone?:
    | "default"
    | "accent"
    | "success"
    | "warning"
    | "destructive"
    | "info"
    | "muted";
}

export interface LancerMechPanelProps
  extends Omit<React.ComponentProps<"div">, "children"> {
  name: string;
  system?: string;
  portrait?: React.ReactNode;
  hp: LancerMechResource;
  heat: LancerMechResource;
  structure: Omit<LancerMechResource, "tone">;
  stress: Omit<LancerMechResource, "tone">;
  repairs?: LancerMechCounter;
  burn?: LancerMechCounter;
  stats?: LancerMechStat[];
  actions?: React.ComponentProps<typeof LancerActionTracker>["actions"];
  onActionToggle?: (id: string) => void;
  onStructurePipClick?: (index: number) => void;
  onStressPipClick?: (index: number) => void;
}

function LancerMechPanel({
  name,
  system,
  portrait,
  hp,
  heat,
  structure,
  stress,
  repairs,
  burn,
  stats = [],
  actions = [],
  onActionToggle,
  onStructurePipClick,
  onStressPipClick,
  className,
  ...props
}: LancerMechPanelProps) {
  return (
    <div
      data-slot="lancer-mech-panel"
      className={cn(
        "grid gap-3 rounded-[5px] border-2 border-border bg-card p-3 shadow-sm",
        className,
      )}
      {...props}
    >
      <header className="flex items-start gap-3">
        <div className="flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-[4px] border border-border bg-muted/30 font-mono text-xs font-bold uppercase text-muted-foreground">
          {portrait ?? "MECH"}
        </div>
        <div className="min-w-0">
          <h2 className="truncate text-sm font-semibold leading-tight text-foreground">
            {name}
          </h2>
          {system ? (
            <p className="font-mono text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              {system}
            </p>
          ) : null}
        </div>
      </header>

      <div className="grid gap-2">
        <LancerResourceBar
          label={hp.label}
          value={hp.value}
          max={hp.max}
          tone={hp.tone ?? "destructive"}
          segments={10}
        />
        <LancerResourceBar
          label={heat.label}
          value={heat.value}
          max={heat.max}
          tone={heat.tone ?? "warning"}
          segments={10}
        />
      </div>

      <div className="grid gap-2 sm:grid-cols-2">
        <LancerStructureStress
          type="structure"
          current={structure.value}
          max={structure.max}
          onPipClick={onStructurePipClick}
        />
        <LancerStructureStress
          type="stress"
          current={stress.value}
          max={stress.max}
          onPipClick={onStressPipClick}
        />
      </div>

      {stats.length > 0 ? (
        <div className="grid grid-cols-3 gap-1.5 sm:grid-cols-6">
          {stats.map((stat) => (
            <LancerStatCard
              key={stat.id}
              label={stat.label}
              value={stat.value}
              tone={stat.tone}
              size="sm"
              decoration="notch"
            />
          ))}
        </div>
      ) : null}

      {actions.length > 0 ? (
        <LancerActionTracker
          label="Action Economy"
          actions={actions}
          onToggle={onActionToggle}
        />
      ) : null}

      <div className="grid grid-cols-2 gap-2">
        <LancerCounter
          label="Repairs"
          value={repairs?.value ?? 0}
          max={repairs?.max ?? 4}
          tone="info"
          size="sm"
        />
        <LancerCounter
          label="Burn"
          value={burn?.value ?? 0}
          max={burn?.max ?? 6}
          tone="destructive"
          size="sm"
        />
      </div>
    </div>
  );
}

export { LancerMechPanel };
